
import React, { useState } from 'react';

/**
 * Prop interface complying with the o-p-e-n.com optimization strategy.
 */
interface OptimizedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
    src: string;          // Main High-Res (Desktop) WebP
    mobileSrc?: string;   // Mobile Optimized WebP (Optional but recommended)
    blurSrc?: string;     // Tiny blurred placeholder (Optional but recommended)
    alt: string;
    className?: string;   // Classes applied to BOTH images
    containerClassName?: string; // Classes for the wrapper div
    priority?: boolean;   // If true, uses eager loading (for hero images)
}

/**
 * OptimizedImage Component
 * 
 * Implements the "Blur Up" technique 1:1 from the research:
 * 1. Shows a tiny blurred placeholder instantly.
 * 2. Loads high-res image in background.
 * 3. Smoothly fades in the high-res image over the placeholder.
 * 4. Uses 'decoding="async"' and proper srcset for responsiveness.
 */
const OptimizedImage: React.FC<OptimizedImageProps> = ({
    src,
    mobileSrc,
    blurSrc,
    alt,
    className = '',
    containerClassName = '',
    priority = false,
    ...props
}) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const imgRef = React.useRef<HTMLImageElement>(null);

    React.useEffect(() => {
        const img = imgRef.current;
        if (img && img.complete) {
            if (img.naturalWidth > 0) {
                setIsLoaded(true);
            }
        }
    }, []);

    // If containerClassName specifies positioning (absolute/fixed), we shouldn't force 'relative'
    // But if it's empty or just sizing, we need a positioning context for the absolute children.
    const hasPositioning = containerClassName.includes('absolute') || containerClassName.includes('fixed') || containerClassName.includes('relative');
    const finalContainerClass = `overflow-hidden ${containerClassName} ${!hasPositioning ? 'relative' : ''}`;

    return (
        <div className={finalContainerClass.trim()}>
            {/* 1. Blur Placeholder - Always visible initially, fades out or stays behind */}
            {/* 1. Blur Placeholder - Always visible behind - NO FADE OUT */}
            {blurSrc && (
                <img
                    src={blurSrc}
                    alt=""
                    aria-hidden="true"
                    className={`
            absolute inset-0 w-full h-full object-cover
            ${className}
          `}
                    style={{
                        filter: 'blur(20px)',
                        transform: 'scale(1.1)', // Prevent blur edge artifacts
                        zIndex: 0
                    }}
                />
            )}

            {/* 2. Main Image - Fades in */}
            <img
                ref={imgRef}
                src={src}
                srcSet={mobileSrc ? `${mobileSrc} 960w, ${src} 1920w` : undefined}
                sizes="100vw" // Force high-quality (1920w) selection by default
                alt={alt}
                decoding="async"
                loading={priority ? "eager" : "lazy"}
                fetchPriority={priority ? "high" : undefined}
                onLoad={() => setIsLoaded(true)}
                onError={() => setIsLoaded(true)}
                className={`
          relative z-10
          transition-opacity duration-0 ease-out
          ${isLoaded || !blurSrc ? 'opacity-100' : 'opacity-0'}
          ${className}
        `}
                {...props}
            />
        </div>
    );
};

export default OptimizedImage;
